import { type z } from 'zod';
import { appConfig } from '@/config/app-config';

/**
 * Чому запит не вдався — у термінах, на які застосунок реально реагує по-різному.
 */
export type HttpFailureKind =
  /** Мережа, таймаут, обірваний запит. */
  | 'network'
  /** 429: перевищено частоту запитів. */
  | 'rate-limited'
  /** 4xx, крім 429. */
  | 'rejected'
  /** 5xx. */
  | 'server'
  /** Відповідь прийшла, але має не ту форму, на яку розрахований клієнт. */
  | 'malformed';

export class HttpFailure extends Error {
  constructor(
    readonly kind: HttpFailureKind,
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'HttpFailure';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  timeoutMs?: number;
  signal?: AbortSignal;
}

/**
 * Виконує запит і розбирає відповідь схемою.
 *
 * Тіло відповіді ніколи не кастується: сервер може віддати помилку у вигляді
 * JSON іншої форми, і без перевірки вона поїхала б далі як валідні дані.
 */
export async function requestJson<T>(
  url: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, timeoutMs = appConfig.httpTimeoutMs, signal } = options;

  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  const onOuterAbort = (): void => {
    controller.abort();
  };
  signal?.addEventListener('abort', onOuterAbort, { once: true });

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      signal: controller.signal,
      ...(body === undefined
        ? {}
        : {
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }),
    });
  } catch (error) {
    const reason = error instanceof Error ? error.message : 'мережева помилка';
    throw new HttpFailure('network', reason);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onOuterAbort);
  }

  if (!response.ok) {
    if (response.status === 429) {
      throw new HttpFailure('rate-limited', 'перевищено частоту запитів', response.status);
    }
    const kind: HttpFailureKind = response.status >= 500 ? 'server' : 'rejected';
    throw new HttpFailure(kind, `сервер відповів ${String(response.status)}`, response.status);
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new HttpFailure('malformed', 'відповідь не є JSON', response.status);
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw new HttpFailure(
      'malformed',
      `несподівана форма відповіді: ${issue?.message ?? 'невідповідність схемі'}`,
      response.status,
    );
  }
  return parsed.data;
}
